import { fromDatetimeLocal, toDatetimeLocal } from '../../helpers/date';
import type { Durations } from './drag';

const MINUTE_MS = 60_000;

/**
 * The four Ist-Zeitpunkte as a form or a DTO carries them. `''` means "not recorded" — the same
 * thing `null` means on the wire. A value is either a full ISO string (the board, the DTOs) or a
 * `<input type="datetime-local">` value (the forms); every function here accepts both and answers
 * in the format it was asked in.
 *
 * WHY INSTANTS AND NOT MINUTES: the server derives the three Ist-Minuten from these four instants
 * (`deriveActualMinutes`) and accepts nothing else, so a client that wants to draw a block or fill a
 * form has to do the same arithmetic in the same direction. This file is that arithmetic, once.
 */
export interface ActualInstants {
  travelStartedAt: string;
  actualStart: string;
  actualEnd: string;
  departureEndedAt: string;
}

const LOCAL_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
/** A full ISO instant with a zone — anything looser is a value still being typed. */
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Epoch ms for either format, `null` for `''` or anything half-typed. Strict on purpose: `new
 * Date('2026-03-')` parses in V8, and a form value mid-keystroke must read as "nothing yet", not
 * as the first of the month.
 */
export function instantMs(value: string): number | null {
  if (!value) return null;
  const iso = LOCAL_RE.test(value) ? fromDatetimeLocal(value) : ISO_RE.test(value) ? value : null;
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** `ms` rendered in the format `like` uses — local for a form value, ISO otherwise. */
function formatLike(ms: number, like: string): string {
  return LOCAL_RE.test(like) ? toDatetimeLocal(new Date(ms)) : new Date(ms).toISOString();
}

/**
 * What the server will derive from these instants — the same rule as `deriveActualMinutes`,
 * mirrored for the live preview.
 *
 * `null` when start or end is missing or unparsable: there is no block to draw, so the caller keeps
 * the last good position rather than guessing. Work is floored at one minute, as on the server.
 *
 * An absent outer instant means "this phase was not recorded". The server then falls back to the
 * **planned** minutes in the occupancy COALESCE (it stores `null`, not 0, when a travel was planned),
 * so the caller passes the planned values as `fallback` to draw what the server will draw. Without a
 * fallback an absent phase counts as 0 — right for the board, which always sends all four.
 */
export function durationsFromInstants(
  instants: ActualInstants,
  fallback: { travel?: number; departure?: number } = {},
): Durations | null {
  const start = instantMs(instants.actualStart);
  const end = instantMs(instants.actualEnd);
  if (start == null || end == null) return null;

  const travelStart = instantMs(instants.travelStartedAt);
  const departureEnd = instantMs(instants.departureEndedAt);

  return {
    travel: travelStart != null ? minutes(travelStart, start) : (fallback.travel ?? 0),
    work: Math.max(1, minutes(start, end)),
    departure: departureEnd != null ? minutes(end, departureEnd) : (fallback.departure ?? 0),
  };
}

/**
 * The inverse: a work start plus three durations, laid out as four instants. This is what a drag
 * on the board or a Plan→Ist copy in a form produces, and because the server re-derives the minutes
 * from exactly these instants, the durations it stores equal the ones that were dragged.
 *
 * All four are always produced, a zero travel or departure as an instant **equal to its
 * neighbour** — a phase of zero length, deliberately not `''`. An absent instant means "not
 * recorded" to the server, which then falls back to the *planned* minutes for that phase; a drag
 * that shrank the Anfahrt of a completed block to nothing would otherwise spring back to the plan on
 * save. Two equal instants derive to an honest 0 on both sides.
 *
 * Answers in the format of `workStartsAt`.
 */
export function instantsFromSlot(workStartsAt: string, durations: Durations): ActualInstants {
  const start = instantMs(workStartsAt);
  if (start == null) {
    return { travelStartedAt: '', actualStart: '', actualEnd: '', departureEndedAt: '' };
  }
  const end = start + durations.work * MINUTE_MS;

  return {
    travelStartedAt: formatLike(start - durations.travel * MINUTE_MS, workStartsAt),
    actualStart: formatLike(start, workStartsAt),
    actualEnd: formatLike(end, workStartsAt),
    departureEndedAt: formatLike(end + durations.departure * MINUTE_MS, workStartsAt),
  };
}

/** Each present instant must not precede the one before it. Returns the offending field, or null. */
export function firstInstantOutOfOrder(
  instants: ActualInstants,
): keyof ActualInstants | null {
  const order: (keyof ActualInstants)[] = [
    'travelStartedAt',
    'actualStart',
    'actualEnd',
    'departureEndedAt',
  ];
  let previous: number | null = null;
  for (const field of order) {
    const ms = instantMs(instants[field]);
    if (ms == null) continue;
    if (previous != null && ms < previous) return field;
    previous = ms;
  }
  return null;
}

const minutes = (fromMs: number, toMs: number) =>
  Math.max(0, Math.round((toMs - fromMs) / MINUTE_MS));
